import type algoliasearchHelper from 'algoliasearch-helper';
import type { InstantSearchStatus } from 'instantsearch.js';
import { useEffect, useState } from 'react';
import { useInstantSearch } from 'react-instantsearch-core';

import { SearchNoResults } from '@/components/SearchNoResults';
import { ProductsList } from '@/components/SearchOverlay/components';
import { StoresList } from '@/components/StoresList';
import { type ContentfulData } from '@/integrations/contentful';
import { HITS_PER_PAGE } from '@/lib/const';

import type { SearchViewType } from '../types';

import * as M from '../messages';
import * as S from './SearchResults.styles';

interface SearchResultsProps {
  activeTab: SearchViewType;
  filteredResultsReady?: boolean;
  hideNoResults?: boolean;
  hitType: SearchViewType;
  initialResults?: any;
  noResultContentData?: ContentfulData[];
  preview?: boolean;
  useScopedResults?: boolean;
}

export const ProductsResults = ({
  activeTab,
  filteredResultsReady = true,
  hideNoResults = false,
  hitType,
  initialResults,
  noResultContentData,
  preview = false,
  useScopedResults = false,
}: SearchResultsProps) => {
  const { results, scopedResults, status } = useInstantSearch();

  const finalResults = useScopedResults
    ? scopedResults.find(
        (item) => item.indexId.toLocaleLowerCase().indexOf(hitType) > -1,
      )?.results
    : results;

  const { data } = useResultInit(finalResults, status);

  if (!data) {
    return null;
  }

  let newResults = initialResults.results[0];
  if (data.results && data.results.nbHits > 0) {
    newResults = data.results;
  }

  if (newResults && newResults.nbHits === 0) {
    return hideNoResults ? null : (
      <SearchNoResults
        data-test-id='ResultsNoResults'
        draftMode={preview}
        noResultContentData={noResultContentData}
      />
    );
  }

  return (
    <>
      <S.Subheader data-test-id='ResultsSubheader'>
        {filteredResultsReady && (
          <>
            {activeTab === 'all' &&
              hitType == 'watches' &&
              newResults?.nbHits > 0 &&
              M.WeFoundWatchNumResults(newResults?.nbHits ?? 0)}
            {activeTab === 'all' &&
              hitType == 'straps' &&
              newResults?.nbHits > 0 &&
              M.WeFoundStrapNumResults(newResults?.nbHits ?? 0)}
            {activeTab === 'all' &&
              hitType == 'eyewear' &&
              newResults?.nbHits > 0 &&
              M.WeFoundEyewearNumResults(newResults?.nbHits ?? 0)}
            {activeTab === 'watches' &&
              M.WeFoundWatchNumResults(newResults?.nbHits ?? 0)}
            {activeTab === 'straps' &&
              M.WeFoundStrapNumResults(newResults?.nbHits ?? 0)}
            {activeTab === 'eyewear' &&
              M.WeFoundEyewearNumResults(newResults?.nbHits ?? 0)}
          </>
        )}
      </S.Subheader>

      <ProductsList
        activeTab={activeTab}
        data={
          data.results && data.results.nbHits > 0 && newResults?.hits
            ? newResults.hits
            : (newResults?.hits || []).slice(0, HITS_PER_PAGE)
        }
        data-test-id='ResultsProductsList'
        hitType={hitType}
        initialResults={newResults}
        itemCount={newResults?.nbHits}
        nbHits={newResults?.nbHits ?? 0}
        preview={preview}
      />
    </>
  );
};

export const StoresResults = ({
  activeTab,
  filteredResultsReady = true,
  hideNoResults = false,
  hitType,
  initialResults,
  noResultContentData,
  preview = false,
  useScopedResults = false,
}: SearchResultsProps) => {
  const { results, scopedResults, status } = useInstantSearch();
  const finalResults = useScopedResults
    ? scopedResults.find((item) => item.indexId === 'stores')?.results
    : results;

  const { data } = useResultInit(finalResults, status);

  if (!data) {
    return null;
  }

  let newResults = initialResults?.results[0];
  if (data.results && data.results?.nbHits > 0) {
    newResults = data.results;
  }

  if (newResults && newResults.nbHits === 0) {
    return hideNoResults ? null : (
      <SearchNoResults
        data-test-id='ResultsNoResults'
        draftMode={preview}
        noResultContentData={noResultContentData}
      />
    );
  }

  return (
    <>
      <S.Subheader data-test-id='StoresResultsSubheader'>
        {filteredResultsReady && (
          <>
            {activeTab === 'all' &&
              hitType == 'stores' &&
              newResults?.nbHits > 0 &&
              M.WeFoundStoresNumResults(newResults?.nbHits ?? 0)}
            {activeTab === 'stores' &&
              M.WeFoundStoresNumResults(newResults?.nbHits ?? 0)}
          </>
        )}
      </S.Subheader>
      <StoresList
        activeTab={activeTab}
        data={
          data.results && data.results.nbHits > 0
            ? newResults?.hits
            : (newResults?.hits.slice(0, HITS_PER_PAGE) ?? [])
        }
        data-test-id='StoresResultsList'
        hitType={hitType}
        initialResults={newResults}
        nbHits={newResults?.nbHits ?? 0}
        preview={preview}
      />
    </>
  );
};

const useResultInit = (
  results: algoliasearchHelper.SearchResults | undefined,
  status: InstantSearchStatus,
) => {
  const [isInited, setIsInited] = useState(false);
  const data = {
    results,
    status,
  };
  useEffect(() => {
    // Mark as initialized when results are available, regardless of queryID
    if (data?.status === 'idle' && data.results) {
      setIsInited(true);
    }
  }, [data]);

  if (!isInited) {
    return {
      loadingStatus: 'Loading',
      data,
    };
  } else if (data.results && data.results?.nbHits === 0) {
    return {
      loadingStatus: 'NoResults',
      data,
    };
  } else {
    return {
      loadingStatus: 'Normal',
      data,
    };
  }
};
